import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, CheckCircle2, Loader2, ShieldCheck, TriangleAlert, MessageSquare, Send, Camera } from "lucide-react";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/public` });

// GPS denied fallback: caller types a what3words code or a landmark; dispatcher resolves it to a pin.
const HintForm = ({ token }) => {
  const [v, setV] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!v.trim() || busy) return;
    setBusy(true);
    try { const { data } = await api.post(`/locate/${token}/hint`, { text: v }); setDone(data.message); }
    catch (e) { alert(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };
  if (done) return <div data-testid="hint-sent" className="text-sm text-[#A7F3D0]">✓ {done}</div>;
  return (
    <div data-testid="hint-form" className="space-y-1.5">
      <div className="text-xs text-[#FDE68A]">Tell the dispatcher where you are: a <b>what3words</b> code (///word.word.word — open the what3words app), or a landmark, store, intersection or bus stop you can see.</div>
      <div className="flex gap-2">
        <input data-testid="hint-text" value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="///filled.count.soap  or  'Sheetz on Rt 51 near the bridge'"
          className="flex-1 min-w-0 bg-[#0B1220] border border-[#FCD34D]/60 rounded px-3 py-2.5 text-sm outline-none text-white" />
        <button data-testid="hint-send" onClick={send} disabled={busy || !v.trim()} className="px-4 rounded bg-[#F59E0B] text-black font-bold disabled:opacity-50">SEND</button>
      </div>
    </div>
  );
};

// Caller Photo Share — camera capture of surroundings, uploaded with the last known GPS fix.
const PhotoShare = ({ token, last }) => {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f, f.name || "photo.jpg");
      if (last) { fd.append("lat", String(last.lat)); fd.append("lng", String(last.lng)); }
      if (note.trim()) fd.append("note", note.trim());
      const { data } = await api.post(`/locate/${token}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent((s) => [...s, { url: URL.createObjectURL(f), ts: new Date() }]); setNote("");
      void data;
    } catch (e2) { setErr(e2.response?.data?.detail || "Could not send the photo. Try again or describe what you see in the chat."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="caller-photo-share" className="bg-[#111A2E] border border-[#1F2A44] rounded p-3 space-y-2">
      <div className="flex items-center gap-2 font-bold text-sm"><Camera size={16} className="text-[#B98A1E]" /> Send a photo of where you are</div>
      <div className="text-[11px] text-[#9CA3AF]">A quick picture of a street sign, storefront or landmark helps responders recognize the spot.</div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="caller-photo-input" />
      <input data-testid="caller-photo-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional: what is in the photo?" className="w-full bg-[#0B1220] border border-[#1F2A44] rounded px-3 py-2 text-sm outline-none" />
      <button data-testid="caller-photo-btn" onClick={() => inputRef.current?.click()} disabled={busy}
        className="w-full py-3 rounded-lg bg-[#1D4ED8] hover:bg-[#1E40AF] font-bold flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />} {busy ? "Sending…" : "TAKE / CHOOSE PHOTO"}
      </button>
      {err && <div className="text-xs text-[#FCA5A5]">{err}</div>}
      {sent.length > 0 && (
        <div data-testid="caller-photo-sent" className="flex gap-2 flex-wrap">
          {sent.map((p, i) => <div key={i} className="relative"><img src={p.url} alt="" className="w-16 h-16 object-cover rounded border border-[#34D399]" /><div className="absolute -bottom-1 -right-1 bg-[#064E3B] rounded-full p-0.5"><CheckCircle2 size={12} /></div></div>)}
          <div className="text-xs text-[#A7F3D0] self-center">Sent to the dispatcher</div>
        </div>
      )}
    </div>
  );
};

// Text From 911: caller ↔ dispatcher chat on the locate page (polls every 4s).
const CallerChat = ({ token }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get(`/locate/${token}/chat`).then((r) => alive && setMsgs(r.data.messages || [])).catch(() => {});
    poll(); const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [token]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try { const { data } = await api.post(`/locate/${token}/chat`, { text }); setMsgs((m) => [...m, data.message]); setText(""); }
    catch (e) { alert(e.response?.data?.detail || "Could not send. Stay on the line with 911."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="caller-chat" className="bg-[#111A2E] border border-[#1F2A44] rounded p-3 space-y-2">
      <div className="flex items-center gap-2 font-bold text-sm"><MessageSquare size={16} className="text-[#B98A1E]" /> Text the dispatcher</div>
      <div className="text-[11px] text-[#9CA3AF]">Can't talk safely? Type here — the dispatcher sees it instantly on your call and can reply.</div>
      <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
        {msgs.length === 0 && <div className="text-xs text-[#6B7280] italic">No messages yet.</div>}
        {msgs.map((m, i) => (
          <div key={i} data-testid={`caller-chat-msg-${m.dir}`} className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${m.dir === "in" ? "ml-auto bg-[#1D4ED8]" : "bg-[#1F2937]"}`}>
            <div className="text-[9px] uppercase tracking-wide opacity-70 mb-0.5">{m.dir === "in" ? "You" : "Communications Dispatcher"} · {new Date(m.ts).toLocaleTimeString()}</div>
            {m.text}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <input data-testid="caller-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message to 911…"
          className="flex-1 min-w-0 bg-[#0B1220] border border-[#1F2A44] rounded px-3 py-2.5 text-sm outline-none focus:border-[#B98A1E]" />
        <button data-testid="caller-chat-send" onClick={send} disabled={busy || !text.trim()} className="px-4 rounded bg-[#DC2626] font-bold disabled:opacity-50 flex items-center"><Send size={16} /></button>
      </div>
    </div>
  );
};

// Caller-facing Text-to-Locate page: /locate/:token — shares precise phone GPS with the 911 dispatcher.
export default function LocateCaller() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [state, setState] = useState("idle"); // idle | locating | sent | denied
  const [last, setLast] = useState(null);
  const [count, setCount] = useState(0);
  const watch = useRef(null);
  useEffect(() => { api.get(`/locate/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(e.response?.data?.detail || "This link is not valid.")); return () => { if (watch.current) navigator.geolocation.clearWatch(watch.current); }; }, [token]);
  const share = () => {
    if (!navigator.geolocation) { setErr("Your phone does not support location sharing in this browser."); return; }
    setState("locating");
    const send = async (pos) => {
      const c = pos.coords;
      try {
        await api.post(`/locate/${token}`, { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy, altitude: c.altitude, heading: c.heading, speed: c.speed, source: "browser" });
        setLast({ lat: c.latitude, lng: c.longitude, acc: c.accuracy, ts: new Date() }); setCount((n) => n + 1); setState("sent");
      } catch (e) { setErr(e.response?.data?.detail || "Could not send location. Please tell the dispatcher your address."); setState("idle"); }
    };
    navigator.geolocation.getCurrentPosition(send, () => setState("denied"), { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
    watch.current = navigator.geolocation.watchPosition(send, () => {}, { enableHighAccuracy: true, maximumAge: 10000 });
  };
  return (
    <div data-testid="locate-caller-page" className="min-h-screen bg-[#0B1220] text-white flex flex-col items-center px-5 py-8" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <div className="w-full max-w-md space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#DC2626] flex items-center justify-center font-black text-lg">911</div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#B98A1E] font-bold">Matcom Office of Public Safety</div>
            <div className="text-lg font-bold leading-tight">Share your location with the dispatcher</div>
          </div>
        </div>
        {err && <div data-testid="locate-error" className="bg-[#7F1D1D] border border-[#FCA5A5] rounded p-3 text-sm flex gap-2"><TriangleAlert size={18} className="shrink-0" /> {err}</div>}
        {info && !err && (
          <>
            <div className="bg-[#111A2E] border border-[#1F2A44] rounded p-4 text-sm space-y-2">
              <div>The communications dispatcher handling your call{info.cc_number ? <> (<b className="font-mono">Case {info.cc_number}</b>)</> : null} has asked for your exact position. Tap the button and allow location access — your phone will send its GPS position directly to the dispatcher.</div>
              <div className="text-[#9CA3AF] text-xs">Only your location is shared. Nothing else from your phone is accessed. Link expires {new Date(info.expires_at).toLocaleTimeString()}.</div>
            </div>
            {state !== "sent" && (
              <button data-testid="locate-share-btn" onClick={share} disabled={state === "locating"}
                className="w-full py-5 rounded-lg bg-[#DC2626] hover:bg-[#b91c1c] font-black text-xl flex items-center justify-center gap-3 disabled:opacity-60">
                {state === "locating" ? <Loader2 size={24} className="animate-spin" /> : <MapPin size={26} />} {state === "locating" ? "Getting your position…" : "SHARE MY LOCATION"}
              </button>
            )}
            {state === "denied" && (
              <div data-testid="locate-denied" className="bg-[#78350F] border border-[#FCD34D] rounded p-3 text-sm space-y-2">
                <div>Location access was blocked. Open your phone's Settings → Location and allow it for this browser, then tap the button again.</div>
                <HintForm token={token} />
              </div>
            )}
            {state !== "denied" && state !== "sent" && <details className="text-xs text-[#9CA3AF]"><summary className="cursor-pointer">Can't share your location? Describe where you are instead</summary><div className="mt-2"><HintForm token={token} /></div></details>}
            {state === "sent" && last && (
              <div data-testid="locate-sent" className="bg-[#064E3B] border border-[#34D399] rounded p-4 space-y-2">
                <div className="flex items-center gap-2 font-bold text-lg"><CheckCircle2 size={22} /> Location sent to the dispatcher</div>
                <div className="font-mono text-xs text-[#A7F3D0]">{last.lat.toFixed(5)}, {last.lng.toFixed(5)} · accuracy ±{Math.round(last.acc)} m · {last.ts.toLocaleTimeString()} · {count} update{count === 1 ? "" : "s"}</div>
                <div className="text-sm">Keep this page open — your position keeps updating automatically if you move. Stay on the line with 911.</div>
              </div>
            )}
            <PhotoShare token={token} last={last} />
            <CallerChat token={token} />
            <div className="flex items-center gap-2 text-[11px] text-[#6B7280]"><ShieldCheck size={14} /> Secure one-time link issued by the Matcom 911 center. If you did not call 911, close this page.</div>
          </>
        )}
      </div>
    </div>
  );
}
